import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useReportsStore } from '@/stores/reports';
import { useUsersStore } from '@/stores/users';
import { useInstitutionsStore } from '@/stores/institutions';
import { useAppointmentsStore } from '@/stores/appointments';
import { isOpenRequest } from '@/utils/appointmentStatus';
import {
  ERouterNames,
  EStatusAppointment,
  EUserRoles,
  INavigationItemPlus,
} from '@/types';

const DASHBOARD_NAV: INavigationItemPlus[] = [
  {
    title: 'nav-panel.dashboard',
    icon: 'Overview',
    key: 'dashboard',
    route: ERouterNames.DASHBOARD,
  },
];

const PATIENT_BASE_NAV: INavigationItemPlus[] = [...DASHBOARD_NAV];

const coordinatorAndProviderBaseNavBuilder: (
  isCoordinator?: boolean,
) => INavigationItemPlus[] = (isCoordinator) => [
  ...DASHBOARD_NAV,
  {
    title: 'common.past-sessions',
    icon: 'ClockFastForward',
    key: 'sessions',
    route: ERouterNames.SESSIONS,
  },
  {
    title: 'common.providers',
    icon: 'Stethoscope',
    key: 'providers',
    route: ERouterNames.PROVIDERS,
  },
  {
    title: isCoordinator ? 'common.patients' : 'nav-panel.my-patients',
    icon: 'UserSlim',
    key: 'patients',
    route: ERouterNames.PATIENTS,
  },
];

const COORDINATOR_SPECIFIC_NAV = {
  scheduleAndOutreach: {
    title: 'nav-panel.schedule-and-outreach',
    icon: 'CalendarWeek',
    route: ERouterNames.SCHEDULE_AND_OUTREACH,
    key: 'schedule-and-outreach',
  },
  commandCenter: {
    title: 'nav-panel.command-center',
    icon: 'Dispatcher',
    route: ERouterNames.COMMAND_CENTER,
    key: 'command-center',
  },
} as const;

/**
 * Custom hook to build and translate navigation items based on the user's role
 * and the current institution's configuration.
 * @returns An object containing:
 *  - availableItems: Navigation items filtered by role and institution settings.
 *  - translatedNavItems: Same items with translated titles using i18n.
 */
export const useNavigationItems = () => {
  const { t } = useTranslation();
  const { checkRole, userData } = useUsersStore((state) => ({
    checkRole: state.checkRole,
    userData: state.userData,
  }));

  const isMedWandEnabled = useInstitutionsStore(
    (state) => !!state.currentInstitution?.configuration?.medwand,
  );

  const openRequestCount = useAppointmentsStore(
    (state) =>
      state.appointments.filter(
        (appointment) =>
          appointment.status?.at(-1)?.action === EStatusAppointment.CONFIRMED &&
          isOpenRequest(appointment.outreachStatus),
      ).length,
  );
  const { reportConfig, getReportConfig } = useReportsStore((state) => ({
    reportConfig: state.reportConfig,
    getReportConfig: state.getReportConfig,
  }));

  useEffect(() => {
    if (checkRole(EUserRoles.ADMIN) || userData?.isSuperAdmin) {
      void getReportConfig();
    }
  }, [checkRole, userData?.isSuperAdmin, getReportConfig]);

  const adminNav: INavigationItemPlus[] = useMemo(() => {
    const createReportSubItems = () => {
      return reportConfig
        .filter((report) => report.visible)
        .map((report) => {
          return {
            title: report.name,
            icon: report.icon ?? '',
            key: report.id,
            route: `${ERouterNames.DATA_REPORTING}/${report.id}`,
          };
        });
    };

    return [
      {
        title: 'common.users',
        icon: 'Users',
        key: 'admin-users',
        route: ERouterNames.USERS_TABLE,
      },
      {
        title: 'common.institution-settings',
        icon: 'SettingsGear',
        key: 'admin-institutions',
        route: ERouterNames.INSTITUTION_SETTINGS,
      },
      {
        title: 'common.document-management',
        icon: 'File',
        key: 'document-management',
        route: ERouterNames.DOCUMENT_MANAGEMENT,
      },
      {
        title: 'common.visit-notes',
        icon: 'Overview',
        key: 'visit-notes',
        route: ERouterNames.VISIT_NOTES_TEMPLATES,
      },
      {
        title: 'nav-panel.data-reporting',
        icon: 'Chart',
        key: 'data-reporting',
        route: ERouterNames.DATA_REPORTING,
        subItems: createReportSubItems(),
      },
    ];
  }, [reportConfig]);

  const availableItems = useMemo(() => {
    if (checkRole(EUserRoles.DEVICE)) return DASHBOARD_NAV;
    if (checkRole(EUserRoles.PATIENT)) {
      const patientNav: INavigationItemPlus[] = [...PATIENT_BASE_NAV];

      if (import.meta.env.VITE_VITAL_SCAN_ENABLED === 'true') {
        patientNav.push({
          title: 'nav-panel.vitals-scan',
          icon: 'Diagram',
          key: 'vitaltrac',
          route: ERouterNames.VITALTRAC,
        });
      }

      if (isMedWandEnabled) {
        patientNav.push({
          title: 'nav-panel.my-data',
          icon: 'Electrocardiogram',
          key: 'my-data',
          route: ERouterNames.MY_DATA,
        });
      }

      return patientNav;
    }

    const result: INavigationItemPlus[] = [];

    if (checkRole(EUserRoles.PROVIDER) || checkRole(EUserRoles.COORDINATOR)) {
      result.push(
        ...coordinatorAndProviderBaseNavBuilder(
          checkRole(EUserRoles.COORDINATOR),
        ),
      );
    }

    if (checkRole(EUserRoles.COORDINATOR)) {
      // TODO: check configuration for this option
      result.push(
        ...Object.values({
          ...COORDINATOR_SPECIFIC_NAV,
          scheduleAndOutreach: {
            ...COORDINATOR_SPECIFIC_NAV.scheduleAndOutreach,
            notificationsAmount:
              openRequestCount > 0 ? openRequestCount : undefined,
          },
        }),
      );
    }

    if (checkRole(EUserRoles.ADMIN) || userData?.isSuperAdmin) {
      result.push(...adminNav);
    }

    return result;
  }, [
    checkRole,
    userData?.isSuperAdmin,
    adminNav,
    openRequestCount,
    isMedWandEnabled,
  ]);

  const translatedNavItems = useMemo(
    () =>
      availableItems.map((item) => ({
        ...item,
        title: t(item.title ?? ''),
        subItems: item.subItems?.map((subItem) => ({
          ...subItem,
          title: t(subItem.title ?? ''),
        })),
      })),
    [availableItems, t],
  );

  return {
    availableItems,
    translatedNavItems,
  };
};
